import { useCallback, useContext, useEffect, useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import dayjs from 'dayjs';
import i18next from 'i18next';

import { SelectionItem, SelectionList } from 'types/api/selection.type';

import { useAddItemMutation } from 'redux/api/ordersApi';
import { useAppSelector } from 'redux/hooks';

import { pdfService } from 'services/pdf.service';
import { selectionDataService } from 'services/selection.service';

import { LayoutContext } from 'contexts';

import { useFetch, useOrderModal } from 'hooks';

import { Item } from './components/Item';

const useList = () => {
  const { id, '*': namePath } = useParams();
  const name = namePath ? decodeURIComponent(namePath) : undefined;
  const { language } = i18next;
  const { orderNumber } = useAppSelector((state) => state.settings);
  const { breadcrumbSet, breadcrumbSecondarySet } = useContext(LayoutContext);
  const { isLoading, content } = useFetch(
    id ? () => selectionDataService.get(id, language) : undefined,
    [id, language]
  );
  const [list, listSet] = useState<SelectionList>();
  const [isPDFLoading, isPDFLoadingSet] = useState<boolean>(false);
  const [isStylePDFLoading, isStylePDFLoadingSet] = useState<boolean>(false);
  const [hasWarning, hasWarningSet] = useState<boolean>(false);
  const { t } = useTranslation();

  const [addItem] = useAddItemMutation();

  const handleOrderSuccess = useCallback(() => {
    if (!content) return;

    const orderId = localStorage.getItem('orderId') ?? '';
    const addPromises = content.map((item: SelectionItem) =>
      addItem({
        orderId,
        data: {
          productCode: item.itemCodes?.split(' ')[1] ?? '',
          mainFabricCode: item.mainFabricCode,
          colorCode: item.itemColorCode,
          sizes: [],
        },
      }).unwrap()
    );

    Promise.all(addPromises)
      .then(() => {
        toast.success(t('selection.addedToOrder'));
      })
      .catch(() => {
        toast.error(t('selection.couldNotAddToOrder'));
      });
  }, [addItem, content]);

  const { isOrderModalVisible, isOrderModalVisibleSet, isOrderLoading, onOrderSubmit } =
    useOrderModal({ onSuccess: handleOrderSuccess });

  const onDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    if (!list) return;

    const newItemsIds = Array.from(list?.itemsIds);
    newItemsIds.splice(source.index, 1);
    newItemsIds.splice(destination.index, 0, Number(draggableId));

    const newItems = {
      ...list,
      itemsIds: newItemsIds,
    };

    const newItemsAPI = newItems?.itemsIds.map((itemId: number, index: number) => {
      const data: SelectionItem = newItems.items[itemId.toString()];
      return {
        id: data.id,
        newIndex: index,
      };
    });

    listSet(newItems);

    if (id) {
      selectionDataService.updateOrder(id, language, newItemsAPI).then((res) => {
        toast.success(t('selection.itemsReordered'));
      });
    }
  };

  const addItemsToOrder = () => {
    if (orderNumber) {
      if (!content || content.length === 0) {
        toast.error(t('selection.noItemsToOrder'));
        return;
      }

      const currentOrderId = localStorage.getItem('orderId') ?? '';
      const addPromises = content.map((item: SelectionItem) =>
        addItem({
          orderId: currentOrderId,
          data: {
            productCode: item.itemCodes?.split(' ')[1] ?? '',
            mainFabricCode: item.mainFabricCode,
            colorCode: item.itemColorCode,
            sizes: [],
          },
        }).unwrap()
      );

      Promise.all(addPromises)
        .then(() => {
          toast.success(t('selection.addedToOrder'));
        })
        .catch((err: unknown) => {
          const error = err as { data?: { apierror?: { message?: string } } };
          toast.error(error.data?.apierror?.message || t('selection.orderFailed'));
        });
    } else {
      isOrderModalVisibleSet(true);
    }
  };

  const mapList = (listData?: SelectionList) =>
    listData?.itemsIds.map((itemId: number) => {
      const data: SelectionItem = listData.items[itemId.toString()];
      return data;
    });

  const getPDF = (asStylePlan: boolean = false) => {
    if (!list?.items) return;

    if (asStylePlan) {
      isStylePDFLoadingSet(true);
    } else {
      isPDFLoadingSet(true);
    }

    const data = {
      name: name!,
      products: mapList(list)!,
      asStylePlan,
      isEnglish: i18next?.language === 'en',
    };

    pdfService
      .getSelectionPDF(data)
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `COLMAR_CATALOGUE_WISHLIST_${dayjs().toDate()}.pdf`);
        document.body.appendChild(link);
        link.click();
        if (asStylePlan) {
          isStylePDFLoadingSet(false);
        } else {
          isPDFLoadingSet(false);
        }
      })
      .catch(() => {
        toast.error(t('pdf.downloadFailed'));
        if (asStylePlan) {
          isStylePDFLoadingSet(false);
        } else {
          isPDFLoadingSet(false);
        }
      });
  };

  const listComponents = mapList(list)?.map((item, index) => (
    <Item index={index} key={item.id} data={item} listSet={listSet} />
  ));

  useEffect(() => {
    const breadcrumb = [
      {
        label: t('selection.myLists'),
      },
      {
        label: name,
      },
    ];

    breadcrumbSet(breadcrumb);

    const breadcrumbSecondary = [
      {
        label: 'Home',
      },
      {
        label: t('selection.myLists'),
      },
      {
        label: name,
      },
    ];

    breadcrumbSecondarySet(breadcrumbSecondary);
  }, [name]);

  useEffect(() => {
    if (content) {
      const listObject: Record<string, SelectionItem> = {};

      content.forEach((item: SelectionItem) => {
        listObject[item.productIndex] = item;
      });

      const state = {
        items: listObject,
        itemsIds: content.map((item: SelectionItem) => item.productIndex),
      };

      listSet(state);
    }
  }, [content]);

  useEffect(() => {
    if (list) {
      const { items } = list;

      // eslint-disable-next-line
      for (const item in list.items) {
        if (items[item].active === false) hasWarningSet(true);
      }
    }
  }, [list]);

  return {
    listComponents,
    onDragEnd,
    isLoading,
    addItemsToOrder,
    isModalVisible: isOrderModalVisible,
    isModalVisibleSet: isOrderModalVisibleSet,
    onOrderSubmit,
    isOrderLoading,
    getPDF,
    isPDFLoading,
    isStylePDFLoading,
    hasWarning,
  };
};

export { useList };
